import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react'
import {
  useIsIndependentUser,
  useIsSessionFollower,
  useIsSessionProvider,
} from '../../hooks/use-sessions-nav-visible'
import { tourStepsFor, upcomingStepsFor, type TourAudience } from './tour-steps'

type Props = {
  /** Called when the user finishes or skips — both persist completion. */
  onClose: () => void
  /** Holds the admin "Premium Session Recording" switch (from the tour context). */
  premiumSessionRecording?: boolean
  /**
   * An Independent marked "Can give consent" on at least one association
   * (`consentGiverFor.length > 0` from the following summary). Swaps in the
   * Alerts step body that explains session-recording consent requests.
   */
  consentGiver?: boolean
}

type Rect = { top: number; left: number; width: number; height: number }

const SPOTLIGHT_PAD = 8
/** Largest the card gets (rem 28); on phones it is the screen width less 2 × 16px. */
const CARD_MAX_WIDTH = 448
const CARD_MARGIN = 12
/** Used until the card has been measured (and in environments without layout). */
const CARD_HEIGHT_GUESS = 340

/**
 * Clear a stale window scroll offset on the native shell before measuring.
 *
 * The native document is overflow:hidden, so window scroll is ALWAYS
 * legitimately (0,0) — but iOS WKWebView can leave the window scrolled after
 * keyboard dismissal / viewport-inset changes (same family as the stale
 * safe-area inset handled in index.css). In that state in-flow targets report
 * client rects shifted by the phantom scroll while the fixed overlay does not,
 * so every spotlight lands offset from its button. Resetting to (0,0) restores
 * the shared coordinate space; inner scrollable lists are unaffected.
 */
function clearStaleNativeWindowScroll(): void {
  if (typeof document === 'undefined' || typeof window === 'undefined') return
  if (!document.documentElement.classList.contains('native-app')) return
  if (window.scrollX !== 0 || window.scrollY !== 0) window.scrollTo(0, 0)
}

/** Return the first visible element matching the selector, else null. */
function findVisibleTarget(selector: string): HTMLElement | null {
  const nodes = document.querySelectorAll<HTMLElement>(selector)
  for (const node of nodes) {
    const rect = node.getBoundingClientRect()
    // NOTE: offsetParent is null for position:fixed elements (e.g. the mobile
    // bottom nav) even when they are fully visible — relying on it alone made
    // every nav-anchored step auto-skip on mobile web, so the tour "flashed and
    // closed". Treat fixed-position elements as visible via computed style.
    const attached =
      node.offsetParent !== null || window.getComputedStyle(node).position === 'fixed'
    const visible = rect.width > 0 && rect.height > 0 && attached
    if (visible) return node
  }
  return null
}

/** Track the user's reduced-motion preference so we can soften every animation. */
function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])

  return reduced
}

/**
 * Lightweight, dependency-free product tour overlay.
 *
 * - Spotlights the live nav/footer controls via `data-tour` selectors, with a
 *   spring-eased spotlight that morphs between targets as steps change.
 * - Skips a step automatically when its target isn't on the current viewport.
 * - Full keyboard support: Esc dismisses, ← / → navigate, focus is trapped.
 * - Honors `prefers-reduced-motion`: animations collapse to instant changes.
 * - Never throws into the app: rendering is guarded and resilient.
 */
export function OnboardingTour({
  onClose,
  premiumSessionRecording = false,
  consentGiver = false,
}: Props) {
  const [index, setIndex] = useState(0)
  // +1 forward, -1 backward — used to skip absent targets in the right direction.
  const [direction, setDirection] = useState<1 | -1>(1)
  const [rect, setRect] = useState<Rect | null>(null)
  // Painted origin of the fixed overlay itself — see the layout effect below.
  const [overlayOrigin, setOverlayOrigin] = useState({ top: 0, left: 0 })
  // Natural (unclipped) height of the card, so it can be placed fully on screen.
  const [cardHeight, setCardHeight] = useState(CARD_HEIGHT_GUESS)
  const overlayRef = useRef<HTMLDivElement>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const primaryBtnRef = useRef<HTMLButtonElement>(null)
  const titleId = useId()
  const bodyId = useId()
  const progressId = useId()
  const reducedMotion = usePrefersReducedMotion()

  const sessionFollower = useIsSessionFollower()
  const provider = useIsSessionProvider()
  const independent = useIsIndependentUser()
  const [steps, setSteps] = useState(() =>
    tourStepsFor({ sessionFollower, provider, independent, premiumSessionRecording, consentGiver }),
  )
  // Audience-gated steps are decided when reached: steps already shown stay
  // fixed, everything after the current one follows the live audience.
  useEffect(() => {
    const audience: TourAudience = {
      sessionFollower,
      provider,
      independent,
      premiumSessionRecording,
      consentGiver,
    }
    setSteps((prev) => upcomingStepsFor(prev, index, audience))
  }, [sessionFollower, provider, independent, premiumSessionRecording, consentGiver, index])

  const total = steps.length
  const step = steps[index]
  // "Step X of N" counts only steps the user will actually see: steps
  // auto-skipped for an absent target, and upcoming steps whose target is not
  // on screen right now, are left out — so N stays stable as they are skipped.
  const [skippedIds, setSkippedIds] = useState<ReadonlySet<string>>(() => new Set())
  const [absentAheadIds, setAbsentAheadIds] = useState<ReadonlySet<string>>(() => new Set())
  useLayoutEffect(() => {
    const absent = new Set<string>()
    steps.forEach((s, i) => {
      if (i > index && s.target && !findVisibleTarget(s.target)) absent.add(s.id)
    })
    setAbsentAheadIds((prev) =>
      prev.size === absent.size && [...absent].every((id) => prev.has(id)) ? prev : absent,
    )
  }, [steps, index])
  const counted = (s: { id: string }) => !skippedIds.has(s.id) && !absentAheadIds.has(s.id)
  const shownTotal = steps.filter(counted).length
  const shownPosition = steps.slice(0, index + 1).filter(counted).length

  const goNext = useCallback(() => {
    setDirection(1)
    setIndex((i) => {
      if (i >= total - 1) {
        onClose()
        return i
      }
      return i + 1
    })
  }, [total, onClose])

  const goPrev = useCallback(() => {
    setDirection(-1)
    setIndex((i) => Math.max(0, i - 1))
  }, [])

  // Resolve the spotlight rect for the current step; skip absent targets.
  useLayoutEffect(() => {
    if (!step) return

    let raf = 0
    // Routes are code-split (React.lazy), so an anchor can mount a beat after
    // navigation while its chunk loads. Keep re-measuring for a short window
    // before declaring the target absent, instead of skipping on frame one.
    const deadline = performance.now() + 500
    const resolve = () => {
      clearStaleNativeWindowScroll()
      const el = findVisibleTarget(step.target)
      if (el) {
        const r = el.getBoundingClientRect()
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
        return
      }
      if (performance.now() < deadline) {
        raf = requestAnimationFrame(resolve)
        return
      }
      // Target not present on this screen — advance/retreat past it instead of
      // stranding the tour. Boundaries finish (forward) or stay (backward).
      setSkippedIds((prev) => (prev.has(step.id) ? prev : new Set(prev).add(step.id)))
      if (direction === 1) {
        if (index >= total - 1) onClose()
        else setIndex((i) => i + 1)
      } else if (index > 0) {
        setIndex((i) => i - 1)
      } else {
        setRect(null)
      }
    }

    // One rAF lets route/layout settle before we measure.
    raf = requestAnimationFrame(resolve)
    return () => cancelAnimationFrame(raf)
  }, [step, index, direction, total, onClose])

  // Keep the spotlight glued to the target on resize / scroll — including
  // visualViewport changes (iOS keyboard dismissal, rotation, pinch), which
  // shift client coordinates without firing a window resize.
  useEffect(() => {
    if (!step) return
    const update = () => {
      clearStaleNativeWindowScroll()
      const el = findVisibleTarget(step.target)
      if (!el) return
      const r = el.getBoundingClientRect()
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height })
    }
    const vv = window.visualViewport
    window.addEventListener('resize', update)
    window.addEventListener('scroll', update, true)
    vv?.addEventListener('resize', update)
    vv?.addEventListener('scroll', update)
    return () => {
      window.removeEventListener('resize', update)
      window.removeEventListener('scroll', update, true)
      vv?.removeEventListener('resize', update)
      vv?.removeEventListener('scroll', update)
    }
  }, [step])

  // Anchor children to the overlay's own painted origin. position:fixed
  // SHOULD pin the overlay at the viewport origin, but iOS WKWebView can
  // paint the fixed layer offset (viewport-inset churn around the first-login
  // keyboard/safe-area work). Target and overlay rects are measured in the
  // same client coordinate space, so placing children at
  // (target − overlayOrigin) stays aligned even when the layer is shifted.
  // On healthy web/Android layouts the origin is (0,0) and this is a no-op.
  useLayoutEffect(() => {
    const el = overlayRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    setOverlayOrigin((prev) =>
      prev.top === r.top && prev.left === r.left ? prev : { top: r.top, left: r.left },
    )
  }, [rect, index])

  // Measure the card's natural height (content + borders, ignoring any
  // max-height clip) so computeCardPosition can keep it entirely on screen.
  useLayoutEffect(() => {
    const el = cardRef.current
    if (!el) return
    const natural = el.scrollHeight + (el.offsetHeight - el.clientHeight)
    if (natural > 0) setCardHeight((prev) => (prev === natural ? prev : natural))
  }, [index, rect, steps])

  // Keyboard: Esc dismisses, arrows navigate, focus stays trapped in the card.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
        return
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault()
        goNext()
        return
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault()
        goPrev()
        return
      }
      if (e.key === 'Tab') {
        const card = cardRef.current
        if (!card) return
        const focusables = card.querySelectorAll<HTMLElement>(
          'button, [href], [tabindex]:not([tabindex="-1"])',
        )
        if (focusables.length === 0) return
        const first = focusables[0]
        const last = focusables[focusables.length - 1]
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault()
          last.focus()
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault()
          first.focus()
        }
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [goNext, goPrev, onClose])

  // Move focus to the primary action whenever the step changes. The card also
  // remounts when it moves from the centered layout to the anchored one (once
  // the first target is measured), so focus has to follow it then too.
  const anchored = rect !== null
  useEffect(() => {
    primaryBtnRef.current?.focus()
  }, [index, anchored])

  if (!step) return null

  const isLast = index === total - 1
  const isFirst = index === 0

  // Direction-aware entrance offset; flattened when reduced motion is on.
  const enterStyle = reducedMotion
    ? undefined
    : ({
        '--tour-enter-x': direction === 1 ? '14px' : '-14px',
        '--tour-enter-y': '8px',
      } as CSSProperties)

  const focusRing =
    'outline-none focus-visible:ring-4 focus-visible:ring-brand-500 focus-visible:ring-offset-2'

  const renderCard = (maxHeight?: number) => (
    <div
      ref={cardRef}
      key={index}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={`${progressId} ${bodyId}`}
      data-tour-card=""
      style={{ ...enterStyle, ...(maxHeight ? { maxHeight } : null) }}
      className={`pointer-events-auto relative w-[calc(100vw-2rem)] max-w-md overflow-y-auto overscroll-contain rounded-2xl border-2 border-slate-300 bg-white p-5 text-slate-900 shadow-[0_20px_60px_-15px_rgba(15,23,42,0.5)] forced-colors:border-[CanvasText] ${
        reducedMotion ? '' : 'animate-tour-card'
      }`}
    >
      <div className="flex items-center justify-between gap-3">
        <span id={progressId} className="text-base font-bold text-brand-800">
          Step {shownPosition} of {shownTotal}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Skip walkthrough"
          className={`-mr-2 inline-flex min-h-12 min-w-12 items-center justify-center rounded-xl px-3 text-base font-semibold text-slate-700 underline underline-offset-4 hover:bg-slate-100 hover:text-slate-900 ${focusRing}`}
        >
          Skip
        </button>
      </div>

      {/* Segmented progress — one bar per counted step, so it agrees with
          "Step X of N". The words above carry the meaning; this is a visual aid. */}
      <div className="mt-1 flex items-center gap-1.5" aria-hidden data-tour-progress="">
        {steps.filter(counted).map((s) => {
          const i = steps.indexOf(s)
          return (
            <span
              key={s.id}
              className={`h-2 flex-1 rounded-full forced-colors:border forced-colors:border-[CanvasText] ${
                i <= index ? 'bg-brand-700 forced-colors:bg-[Highlight]' : 'bg-slate-300'
              }`}
            />
          )
        })}
      </div>

      <h2 id={titleId} className="mt-4 text-2xl font-bold tracking-tight text-slate-900">
        {step.title}
      </h2>
      <p
        id={bodyId}
        tabIndex={0}
        className={`mt-2 rounded-md text-base leading-relaxed text-slate-800 ${focusRing}`}
      >
        {step.body}
      </p>

      <div className="mt-5 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={goPrev}
          disabled={isFirst}
          className={`min-h-12 min-w-24 rounded-xl border-2 border-slate-400 bg-white px-4 py-2 text-base font-semibold text-slate-800 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white ${focusRing}`}
        >
          Back
        </button>
        <button
          ref={primaryBtnRef}
          type="button"
          onClick={goNext}
          className={`inline-flex min-h-12 min-w-28 items-center justify-center rounded-xl bg-brand-700 px-6 py-2 text-base font-bold text-white hover:bg-brand-800 forced-colors:border-2 forced-colors:border-[ButtonText] ${focusRing}`}
        >
          {isLast ? 'Done' : 'Next'}
        </button>
      </div>
    </div>
  )

  // Centered card until the target is measured (or when stepping back onto a
  // first step whose target has gone).
  if (!rect) {
    return (
      <div
        data-onboarding-tour=""
        className={`fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/70 p-4 ${
          reducedMotion ? '' : 'animate-tour-backdrop'
        }`}
      >
        {renderCard(window.innerHeight - 32)}
      </div>
    )
  }

  // Anchored layout: dim everything except a spotlight around the target.
  // Coordinates are relative to the overlay's own painted origin (normally
  // 0,0 — see the overlayOrigin effect for the iOS WKWebView offset case).
  const spot: Rect = {
    top: rect.top - overlayOrigin.top - SPOTLIGHT_PAD,
    left: rect.left - overlayOrigin.left - SPOTLIGHT_PAD,
    width: rect.width + SPOTLIGHT_PAD * 2,
    height: rect.height + SPOTLIGHT_PAD * 2,
  }

  const placement = computeCardPosition(spot, cardHeight)

  return (
    <div ref={overlayRef} data-onboarding-tour="" className="fixed inset-0 z-[120]">
      {/* Click shield — blocks the rest of the app while the tour is open. */}
      <div className="absolute inset-0" aria-hidden />
      {/* Spotlight: the big box-shadow dims everything around the hole; it
          morphs between targets via the spring-eased transition. (Forced-colors
          mode drops box-shadows, so the white/Highlight border below carries
          the spotlight there.) */}
      <div
        aria-hidden
        className={`pointer-events-none absolute rounded-2xl ${
          reducedMotion ? '' : 'tour-spotlight-morph'
        }`}
        style={{
          top: spot.top,
          left: spot.left,
          width: spot.width,
          height: spot.height,
          boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.72)',
        }}
      />
      {/* Solid white outline (breathing violet halo unless reduced motion). */}
      <div
        aria-hidden
        data-tour-spotlight=""
        className={`pointer-events-none absolute rounded-2xl border-[3px] border-white forced-colors:border-[Highlight] ${
          reducedMotion ? '' : 'tour-spotlight-morph tour-spotlight-ring'
        }`}
        style={{
          top: spot.top,
          left: spot.left,
          width: spot.width,
          height: spot.height,
        }}
      />
      <div
        className={`pointer-events-none absolute ${reducedMotion ? '' : 'tour-card-morph'}`}
        style={{ top: placement.top, left: placement.left }}
      >
        {renderCard(placement.maxHeight)}
      </div>
    </div>
  )
}

/**
 * Place the card on screen next to the spotlight, using its measured height:
 * beside the target on the desktop rail (a vertical strip on the left), else
 * above or below it (below when it fits, otherwise whichever side has more
 * room, shrinking + scrolling the card on very short screens).
 */
export function computeCardPosition(
  spot: Rect,
  cardHeight: number,
  viewport: { width: number; height: number } = { width: window.innerWidth, height: window.innerHeight },
): { top: number; left: number; maxHeight: number } {
  const { width: vw, height: vh } = viewport
  const width = Math.min(vw - 32, CARD_MAX_WIDTH)
  const spotRight = spot.left + spot.width
  const spotBottom = spot.top + spot.height

  // Desktop icon rail: sit to the right of the target, centred on it.
  if (vw >= 768 && spotRight < 160) {
    const maxHeight = vh - CARD_MARGIN * 2
    const h = Math.min(cardHeight, maxHeight)
    return {
      top: clamp(spot.top + spot.height / 2 - h / 2, CARD_MARGIN, vh - CARD_MARGIN - h),
      left: spotRight + 16,
      maxHeight,
    }
  }

  const left = clamp(spot.left + spot.width / 2 - width / 2, 16, vw - 16 - width)
  const roomBelow = vh - spotBottom - CARD_MARGIN * 2
  const roomAbove = spot.top - CARD_MARGIN * 2
  const fitsBelow = cardHeight <= roomBelow
  const fitsAbove = cardHeight <= roomAbove
  // Below by default; above when it fits and the target is in the lower half
  // of the screen (the mobile bottom nav), which reads more naturally.
  if (fitsBelow && !(fitsAbove && spot.top > vh / 2)) {
    return { top: spotBottom + CARD_MARGIN, left, maxHeight: roomBelow }
  }
  if (fitsAbove) {
    return { top: spot.top - CARD_MARGIN - cardHeight, left, maxHeight: roomAbove }
  }
  // Neither side fits the whole card: use the larger one and scroll inside it.
  if (roomBelow >= roomAbove) {
    const maxHeight = Math.min(Math.max(roomBelow, 120), vh - CARD_MARGIN * 2)
    return { top: Math.min(spotBottom + CARD_MARGIN, vh - CARD_MARGIN - maxHeight), left, maxHeight }
  }
  const maxHeight = Math.min(Math.max(roomAbove, 120), vh - CARD_MARGIN * 2)
  return { top: Math.max(CARD_MARGIN, spot.top - CARD_MARGIN - maxHeight), left, maxHeight }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}
